import { useEffect } from 'react';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  VStack,
  ModalBody,
  RedAsterix,
  Input,
} from '@wegroup/design-system';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CurrencyDurationRightAddon from '@advisory-report/src/features/creation-flow/components/insurance-policies/shared/modal/CurrencyDurationRightAddon';
import AnvaCollectionMethodSelectInput from '@flows/src/components/flow-products/quotes/basket/AnvaCollectionMethodSelectInput';

type Fields = 'start_date' | 'payment_interval' | 'anva_collection_method_code';

interface Props {
  hiddenFields?: Fields[];
  type?: 'POLICY' | 'PACKAGE';
  isPaymentMethodRequired?: boolean;
}

const AnvaPackageItemEditModalBody = ({
  hiddenFields,
  type,
  isPaymentMethodRequired = false,
}: Props) => {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext();

  // Watch for payment interval changes
  const paymentInterval = watch('payment_interval');

  // Check if direct debit is required (monthly payments)
  const isDirectDebitRequired =
    paymentInterval && paymentInterval === 'MONTHLY';

  useEffect(() => {
    if (isDirectDebitRequired) {
      // Set appropriate direct debit collection method based on type
      const directDebitCode = type === 'PACKAGE' ? '48' : '28';
      setValue('anva_collection_method_code', directDebitCode);
    }
  }, [paymentInterval, setValue, type, isDirectDebitRequired]);

  const handleChangeIncasso = (value: string) => {
    setValue('anva_collection_method_code', value, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  return (
    <ModalBody>
      <VStack align="stretch" spacing="4">
        {!hiddenFields?.includes('start_date') && (
          <Controller
            name="start_date"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl isInvalid={!!error}>
                <FormLabel>
                  {t(
                    'ADVISORY-REPORTS_CURRENT-INSURANCE_MODAL_DETAIL_START-DATE',
                  )}
                </FormLabel>
                <Input
                  type="date"
                  value={value || ''}
                  onChange={(e) => onChange(e.currentTarget.value || undefined)}
                />
                {error?.message && (
                  <FormErrorMessage>{t(error.message)}</FormErrorMessage>
                )}
              </FormControl>
            )}
          />
        )}
        {!hiddenFields?.includes('payment_interval') && (
          <Controller
            name="payment_interval"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl isInvalid={!!error}>
                <FormLabel>{t('Payment interval')}</FormLabel>
                <CurrencyDurationRightAddon
                  value={value}
                  onChange={(e) => onChange(e.currentTarget.value)}
                  borderLeft={undefined}
                  borderRight={undefined}
                  borderTopLeftRadius={undefined}
                  borderBottomLeftRadius={undefined}
                  excludedValues={['ONCE']}
                />
                {error?.message && (
                  <FormErrorMessage>{t(error.message)}</FormErrorMessage>
                )}
              </FormControl>
            )}
          />
        )}
        {!hiddenFields?.includes('anva_collection_method_code') && (
          <Controller
            name="anva_collection_method_code"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl isInvalid={!!error}>
                <FormLabel>
                  {t('COLLECTION-METHOD')}{' '}
                  {isPaymentMethodRequired && <RedAsterix />}
                </FormLabel>
                <AnvaCollectionMethodSelectInput
                  key={value ?? 'incasso-select'}
                  value={value?.toString()}
                  onChange={(e) => handleChangeIncasso(e.currentTarget.value)}
                  type={type}
                  isDisabled={isDirectDebitRequired}
                />
                {error?.message && (
                  <FormErrorMessage>{t(error.message)}</FormErrorMessage>
                )}
              </FormControl>
            )}
          />
        )}
      </VStack>
    </ModalBody>
  );
};

export default AnvaPackageItemEditModalBody;
